import React, { useMemo, useState } from 'react';
import { Icon } from '@cotera/client/app/components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { CopyButton } from './components/copy-button';

export interface WebhookTriggerRun {
  id: string;
  webhookId: string;
  status: string;
  createdAt: string;
  completionResult: string | null;
  completionFailure: string | null;
}

export interface WebhookTriggerMeta {
  webhookId: string;
  name: string;
  ownerOrgId: string;
  organizationName: string | null;
  externalSubscriptionType: string | null;
  providerKey: string;
  installStatus: string;
  enabled: boolean;
  createdAt: string;
}

interface WebhookTriggersTableProps {
  webhooks: WebhookTriggerMeta[];
  runs: WebhookTriggerRun[];
  restateUrl: string | null;
}

interface WebhookGroup {
  meta: WebhookTriggerMeta;
  successCount: number;
  failureCount: number;
  otherCount: number;
  total: number;
  runs: WebhookTriggerRun[];
}

function getRunStatusLabel(
  status: string,
  completionResult: string | null
): { label: string; color: string } {
  if (status !== 'completed') {
    return { label: status, color: 'text-yellow-600' };
  }
  if (completionResult === 'success') {
    return { label: 'success', color: 'text-green-600' };
  }
  if (completionResult === 'failure') {
    return { label: 'failure', color: 'text-red-600' };
  }
  return { label: completionResult ?? 'completed', color: 'text-gray-600' };
}

const installStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'text-green-600';
    case 'error':
      return 'text-red-600';
    case 'installing':
      return 'text-yellow-600';
    case 'archived':
      return 'text-gray-400';
    default:
      return 'text-gray-600';
  }
};

export const WebhookTriggersTable: React.FC<WebhookTriggersTableProps> = ({
  webhooks,
  runs,
  restateUrl,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const groups = useMemo<WebhookGroup[]>(() => {
    // Index metadata by webhook id, synthesizing a placeholder for any webhook
    // that fired but is no longer in the registry (deleted / beyond the limit).
    const metaById = new Map<string, WebhookTriggerMeta>(
      webhooks.map((w) => [w.webhookId, w])
    );
    const runsByWebhook = new Map<string, WebhookTriggerRun[]>();
    for (const run of runs) {
      const list = runsByWebhook.get(run.webhookId);
      if (list === undefined) {
        runsByWebhook.set(run.webhookId, [run]);
      } else {
        list.push(run);
      }
      if (!metaById.has(run.webhookId)) {
        metaById.set(run.webhookId, {
          webhookId: run.webhookId,
          name: 'Unknown webhook',
          ownerOrgId: '',
          organizationName: null,
          externalSubscriptionType: null,
          providerKey: 'unknown',
          installStatus: 'unknown',
          enabled: false,
          createdAt: run.createdAt,
        });
      }
    }

    const result: WebhookGroup[] = [];
    for (const meta of metaById.values()) {
      const webhookRuns = runsByWebhook.get(meta.webhookId) ?? [];
      let successCount = 0;
      let failureCount = 0;
      let otherCount = 0;
      for (const run of webhookRuns) {
        if (run.completionResult === 'success') {
          successCount++;
        } else if (run.completionResult === 'failure') {
          failureCount++;
        } else {
          otherCount++;
        }
      }
      result.push({
        meta,
        successCount,
        failureCount,
        otherCount,
        total: webhookRuns.length,
        runs: webhookRuns,
      });
    }
    // Webhooks that actually fired float to the top; ties broken by name.
    return result.sort(
      (a, b) => b.total - a.total || a.meta.name.localeCompare(b.meta.name)
    );
  }, [webhooks, runs]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === '') {
      return groups;
    }
    return groups.filter((g) => {
      const haystack = [
        g.meta.name,
        g.meta.webhookId,
        g.meta.organizationName ?? '',
        g.meta.externalSubscriptionType ?? '',
        g.meta.providerKey,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [groups, filter]);

  const toggle = (webhookId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(webhookId)) {
        next.delete(webhookId);
      } else {
        next.add(webhookId);
      }
      return next;
    });
  };

  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No webhook triggers found in this time range.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Webhook Triggers
        </h3>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name, org, type…"
          className="w-64 rounded-md border px-2 py-1 text-sm"
        />
      </div>
      <div className="bg-white rounded-lg shadow overflow-auto max-h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Webhook</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Subscription Type</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Install</TableHead>
              <TableHead>Success</TableHead>
              <TableHead>Failures</TableHead>
              <TableHead>Other</TableHead>
              <TableHead>Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((group) => {
              const { meta } = group;
              const isExpanded = expanded.has(meta.webhookId);
              return (
                <React.Fragment key={meta.webhookId}>
                  <TableRow
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => toggle(meta.webhookId)}
                  >
                    <TableCell>
                      <Icon
                        icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                        className="w-4 h-4 text-gray-500"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {meta.name}
                        </span>
                        <span className="inline-flex items-center gap-1 font-mono text-xs text-gray-400">
                          {meta.webhookId}
                          <CopyButton
                            value={meta.webhookId}
                            title="Copy webhook ID"
                          />
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700">
                      {meta.organizationName ??
                        (meta.ownerOrgId !== '' ? (
                          <span className="font-mono text-xs">
                            {meta.ownerOrgId}
                          </span>
                        ) : (
                          <span className="text-gray-400">Unknown</span>
                        ))}
                    </TableCell>
                    <TableCell className="text-sm">
                      {meta.externalSubscriptionType !== null ? (
                        <span className="font-mono text-xs">
                          {meta.externalSubscriptionType}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-700">
                      {meta.providerKey}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-xs font-medium ${installStatusColor(
                          meta.installStatus
                        )}`}
                      >
                        {meta.installStatus}
                      </span>
                      {!meta.enabled && (
                        <span className="ml-1 text-xs text-gray-400">
                          (disabled)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-green-600 font-medium">
                      {group.successCount}
                    </TableCell>
                    <TableCell
                      className={
                        group.failureCount > 0
                          ? 'text-red-600 font-semibold'
                          : ''
                      }
                    >
                      {group.failureCount}
                    </TableCell>
                    <TableCell
                      className={
                        group.otherCount > 0
                          ? 'text-yellow-600 font-medium'
                          : ''
                      }
                    >
                      {group.otherCount}
                    </TableCell>
                    <TableCell className="font-medium">{group.total}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={10} className="p-0">
                        <div className="bg-gray-50 border-y">
                          {group.runs.length === 0 ? (
                            <p className="p-4 text-sm text-muted-foreground">
                              No runs for this webhook in this time range.
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Created</TableHead>
                                  <TableHead>Failure</TableHead>
                                  <TableHead>Invocation</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.runs.map((run) => {
                                  const { label, color } = getRunStatusLabel(
                                    run.status,
                                    run.completionResult
                                  );
                                  const shortId = `${run.id.substring(
                                    0,
                                    12
                                  )}...`;
                                  return (
                                    <TableRow key={run.id}>
                                      <TableCell>
                                        <span
                                          className={`font-medium ${color}`}
                                        >
                                          {label}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-xs text-gray-600">
                                        {run.createdAt}
                                      </TableCell>
                                      <TableCell>
                                        {run.completionFailure !== null ? (
                                          <span
                                            className="text-xs text-red-600"
                                            title={run.completionFailure}
                                          >
                                            {run.completionFailure.length > 80
                                              ? `${run.completionFailure.substring(
                                                  0,
                                                  80
                                                )}...`
                                              : run.completionFailure}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">
                                            —
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {restateUrl !== null ? (
                                          <a
                                            href={`${restateUrl}/ui/invocations/${run.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-xs text-blue-600 hover:underline"
                                          >
                                            {shortId}
                                          </a>
                                        ) : (
                                          <span className="font-mono text-xs text-gray-500">
                                            {shortId}
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
